// src/pages/Appli/AppliAdmin/dashboard/VisitesPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { countVisites, countVisitesByDate } from "../../../services/visitesServices";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* ===== Helpers (timezone-safe) ===== */
const parseYMD = (ymd) => {
  // "YYYY-MM-DD" -> Date (local)
  const [y, m, d] = String(ymd).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const ymdFromDate = (date) => {
  // Date (local) -> "YYYY-MM-DD" (local)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const startOfToday = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

const fmtDate = (d) => {
  const dd = typeof d === "string" ? parseYMD(d) : d;
  return dd.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

const fillMissingDates = (rows, { includeToday = true } = {}) => {
  const arr = Array.isArray(rows) ? rows : [];
  const today = startOfToday();

  if (!arr.length) {
    // optionnel : afficher quand même aujourd’hui à 0
    return includeToday ? [{ date_visite: ymdFromDate(today), compteur: 0 }] : [];
  }

  const byDate = new Map(
    arr.map((r) => [r.date_visite, Number(r.compteur || 0)])
  );

  const min = parseYMD(arr[0].date_visite);
  const last = parseYMD(arr[arr.length - 1].date_visite);

  const max = includeToday && today > last ? today : last;

  const out = [];
  for (let d = new Date(min); d <= max; d.setDate(d.getDate() + 1)) {
    const key = ymdFromDate(d);
    out.push({ date_visite: key, compteur: byDate.get(key) ?? 0 });
  }
  return out;
};

const sum = (arr, sel = (x) => x) => arr.reduce((acc, v) => acc + sel(v), 0);

/* ===== Component ===== */
const VisitesPanel = () => {
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [total, setTotal] = useState(0);
  const [series, setSeries] = useState([]);
  const [range, setRange] = useState("30"); // "7" | "30" | "90" | "all"

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErrorMsg("");
        const [tot, byDate] = await Promise.all([countVisites(), countVisitesByDate()]);
        if (!mounted) return;

        const arr = Array.isArray(byDate) ? byDate.slice() : [];
        arr.sort((a, b) => String(a.date_visite).localeCompare(String(b.date_visite)));

        // ✅ important : inclure aujourd’hui même si l’API ne renvoie pas la ligne
        setSeries(fillMissingDates(arr, { includeToday: true }));
        setTotal(Number(tot?.count || 0));
      } catch (e) {
        console.error(e);
        if (mounted) {
          setSeries([]);
          setTotal(0);
          setErrorMsg("Impossible de charger les statistiques de visites.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (range === "all") return series;
    const n = parseInt(range, 10);
    return series.slice(Math.max(0, series.length - n));
  }, [series, range]);

  const kpis = useMemo(() => {
    if (!filtered.length) {
      return { periodTotal: 0, periodAvg: 0, from: null, to: null, days: 0, today: 0, last7: 0, last30: 0 };
    }
    const periodTotal = sum(filtered, (x) => x.compteur);
    const days = filtered.length;
    const periodAvg = days ? Math.round((periodTotal / days) * 10) / 10 : 0;
    const from = filtered[0]?.date_visite || null;
    const to = filtered[filtered.length - 1]?.date_visite || null;
    const today = filtered[filtered.length - 1]?.compteur || 0;
    const last7 = sum(filtered.slice(-7), (x) => x.compteur);
    const last30 = sum(filtered.slice(-30), (x) => x.compteur);
    return { periodTotal, periodAvg, from, to, days, today, last7, last30 };
  }, [filtered]);

  return (
    <section className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80 p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-semibold">Visites</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Total: <span className="font-semibold">{total.toLocaleString("fr-FR")}</span>
          </p>
        </div>

        <div className="inline-flex rounded-xl ring-1 ring-slate-300 dark:ring-slate-600 overflow-hidden">
          {[
            { id: "7", label: "7j" },
            { id: "30", label: "30j" },
            { id: "90", label: "90j" },
            { id: "all", label: "Tout" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setRange(opt.id)}
              className={[
                "px-3 py-1.5 text-sm",
                range === opt.id
                  ? "bg-indigo-600 text-white"
                  : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200",
              ].join(" ")}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {errorMsg && (
        <div className="mt-3 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 text-sm">
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Période</div>
          <div className="mt-1 text-lg font-semibold">
            {kpis.from && kpis.to ? `${fmtDate(kpis.from)} → ${fmtDate(kpis.to)}` : "—"}
          </div>
          <div className="text-xs text-slate-500">{kpis.days} jours</div>
        </div>

        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Visites sur la période</div>
          <div className="mt-1 text-2xl font-semibold">{kpis.periodTotal.toLocaleString("fr-FR")}</div>
          <div className="text-xs text-slate-500">Moyenne {kpis.periodAvg}/j</div>
        </div>

        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Aujourd’hui</div>
          <div className="mt-1 text-2xl font-semibold">{kpis.today}</div>
          <div className="text-xs text-slate-500">Dernier point de la série</div>
        </div>

        <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">7j / 30j (glissants)</div>
          <div className="mt-1 text-lg font-semibold">
            {kpis.last7} <span className="text-xs text-slate-500"> / </span> {kpis.last30}
          </div>
          <div className="text-xs text-slate-500">Sommes partielles</div>
        </div>
      </div>

      <div className="mt-5 h-[300px] w-full rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 p-2">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center text-slate-500">Aucune donnée à afficher</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filtered.map((d) => ({ date: fmtDate(d.date_visite), value: d.compteur }))}
              margin={{ left: 12, right: 12, top: 10, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [v, "Visites"]} labelFormatter={(label) => `Date: ${label}`} />
              <Line type="monotone" dataKey="value" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
};

export default VisitesPanel;
